/**
 * TaskTypeFilter - Hierarchical task type filter with group organization
 *
 * Features:
 * - Groups displayed as section headers
 * - Types displayed as checkboxes under each group
 * - "Select All" button for groups with multiple types
 * - Types without groups shown under "Other"
 *
 * Data Structure:
 * The groupCounts prop contains:
 * - groups: [{ canonical_group: "Intake", count: 22 }, ...]
 * - types: [{ group: "Intake", type: "Conduct Intake", count: 12 }, ...]
 */

import { useMemo } from 'react';

import {
  GROUP_DISPLAY_NAMES,
  GROUP_DISPLAY_ORDER,
} from '@/components/tasks-datatable/config/taskVerticals';
import { cn } from '@/lib/utils';
import type {
  TaskCountsResponse,
  TypeCount,
} from '@/services/api/tasksService';

/** Type data for display within a group (excludes group since it's the map key) */
type GroupedTypeData = Omit<TypeCount, 'group'>;

interface TaskTypeFilterProps {
  groupCounts?: TaskCountsResponse;
  selectedTypes: string[];
  selectedGroups: string[];
  onChange: (types: string[], groups: string[]) => void;
}

export const TaskTypeFilter = ({
  groupCounts,
  selectedTypes,
  selectedGroups,
  onChange,
}: TaskTypeFilterProps) => {
  // Organize types by group
  const groupedTypes = useMemo(() => {
    if (!groupCounts?.types) return new Map<string, GroupedTypeData[]>();

    const map = new Map<string, GroupedTypeData[]>();

    // Sort types into groups
    groupCounts.types.forEach((typeData) => {
      const groupName = typeData.group;
      if (!map.has(groupName)) {
        map.set(groupName, []);
      }
      map.get(groupName)?.push({
        type: typeData.type,
        count: typeData.count,
        id: typeData.id,
      });
    });

    // Sort types within each group alphabetically
    map.forEach((types) => {
      types.sort((a, b) => a.type.localeCompare(b.type));
    });

    return map;
  }, [groupCounts?.types]);

  // Get ordered groups that have types
  const orderedGroups = useMemo(() => {
    const groups: string[] = [];

    // Add groups in preferred order
    GROUP_DISPLAY_ORDER.forEach((group) => {
      if (groupedTypes.has(group)) {
        groups.push(group);
      }
    });

    // Add any remaining groups not in the order
    groupedTypes.forEach((_, group) => {
      if (!groups.includes(group)) {
        groups.push(group);
      }
    });

    return groups;
  }, [groupedTypes]);

  // Handle individual type toggle (uses ID for selection)
  const handleTypeToggle = (id: string) => {
    const newTypes = selectedTypes.includes(id)
      ? selectedTypes.filter((t) => t !== id)
      : [...selectedTypes, id];
    onChange(newTypes, selectedGroups);
  };

  // Handle "Select All" for a group (uses IDs for selection)
  const handleGroupSelectAll = (groupName: string) => {
    const groupTypes = groupedTypes.get(groupName) || [];
    const groupTypeIds = groupTypes.map((t) => t.id);
    const allSelected = groupTypeIds.every((id) => selectedTypes.includes(id));

    let newTypes: string[];
    if (allSelected) {
      // Deselect all types in this group
      newTypes = selectedTypes.filter((t) => !groupTypeIds.includes(t));
    } else {
      // Select all types in this group
      const idsToAdd = groupTypeIds.filter((id) => !selectedTypes.includes(id));
      newTypes = [...selectedTypes, ...idsToAdd];
    }

    onChange(newTypes, selectedGroups);
  };

  // Check if all types in a group are selected (by ID)
  const isGroupAllSelected = (groupName: string): boolean => {
    const groupTypes = groupedTypes.get(groupName) || [];
    if (groupTypes.length === 0) return false;
    return groupTypes.every((t) => selectedTypes.includes(t.id));
  };

  if (orderedGroups.length === 0) {
    return (
      <p className="text-xs text-zinc-500 dark:text-zinc-400">
        No task types available
      </p>
    );
  }

  return (
    <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
      {orderedGroups.map((groupName) => {
        const types = groupedTypes.get(groupName) || [];
        const displayName =
          GROUP_DISPLAY_NAMES[groupName as keyof typeof GROUP_DISPLAY_NAMES] ||
          groupName;
        const showSelectAll = types.length > 1;

        return (
          <div key={groupName} className="space-y-2">
            {/* Group Header */}
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                {displayName}
              </span>
              {showSelectAll && (
                <button
                  type="button"
                  onClick={() => handleGroupSelectAll(groupName)}
                  className="text-xs text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white transition-colors"
                >
                  {isGroupAllSelected(groupName)
                    ? 'Deselect all'
                    : 'Select all'}
                </button>
              )}
            </div>

            {/* Types as Checkboxes */}
            <div className="space-y-1.5">
              {types.map(({ type, id }) => {
                const isSelected = selectedTypes.includes(id);
                const checkboxId = `task-type-${id}`;
                return (
                  <label
                    key={id}
                    htmlFor={checkboxId}
                    className="flex items-center gap-2 cursor-pointer group"
                  >
                    <input
                      id={checkboxId}
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleTypeToggle(id)}
                      className="h-3.5 w-3.5 rounded border-zinc-950/20 dark:border-white/20 text-zinc-700 dark:text-zinc-300 focus:ring-zinc-500 dark:bg-zinc-900 cursor-pointer"
                    />
                    <span
                      className={cn(
                        'text-xs',
                        isSelected
                          ? 'text-zinc-950 dark:text-white font-medium'
                          : 'text-zinc-600 dark:text-zinc-400 group-hover:text-zinc-900 dark:group-hover:text-white',
                      )}
                    >
                      {type}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};
